/**
 * The one way this app talks to the adapter.
 *
 * Shaped after `zs-booking/src/common/helpers/api.ts` — fetch, a bearer from
 * localStorage, same-origin by default — with one deliberate change: a failed
 * request THROWS rather than returning the error body as a value. In the
 * original, every caller has to remember to check `success`, and forgetting is
 * silent: the screen renders `undefined` and the user sees an empty table
 * instead of "we could not reach your calendar". Throwing makes the failure
 * impossible to skip past.
 */

export interface ApiEnvelope<T> {
  status: number;
  success: boolean;
  message: string;
  data: T;
  count?: number;
  meta?: PaginationMeta;
  timestamp: string;
}

export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasPrevious: boolean;
  hasNext: boolean;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code?: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** True when the session is the problem, so the UI should sign the user out. */
  get isAuthFailure(): boolean {
    return this.status === 401;
  }
}

const TOKEN_KEY = 'auth_token';
const REFRESH_KEY = 'refresh_token';
const ORG_KEY = 'organization_id';

export const tokenStore = {
  token: () => localStorage.getItem(TOKEN_KEY),
  refreshToken: () => localStorage.getItem(REFRESH_KEY),
  organizationId: () => localStorage.getItem(ORG_KEY),

  setSession(accessToken: string, refreshToken?: string | null) {
    localStorage.setItem(TOKEN_KEY, accessToken);
    if (refreshToken) localStorage.setItem(REFRESH_KEY, refreshToken);
  },
  setOrganization(organizationId: string | null) {
    if (organizationId) localStorage.setItem(ORG_KEY, organizationId);
    else localStorage.removeItem(ORG_KEY);
  },
  clear() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(ORG_KEY);
  },
};

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  data?: unknown;
  /** Skip the bearer — used by the sign-in calls themselves. */
  anonymous?: boolean;
  /** Skip the organization header — used by /auth routes, which are user-scoped. */
  withoutOrganization?: boolean;
  signal?: AbortSignal;
}

const BASE = '/api/v1';

/** In-flight refresh, so five parallel 401s cause one refresh and not five. */
let refreshInFlight: Promise<boolean> | null = null;

async function attemptRefresh(): Promise<boolean> {
  const refreshToken = tokenStore.refreshToken();
  if (!refreshToken) return false;

  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const response = await fetch(`${BASE}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: refreshToken }),
        });
        if (!response.ok) return false;
        const envelope = (await response.json()) as ApiEnvelope<{
          access_token: string;
          refresh_token?: string;
        }>;
        if (!envelope?.data?.access_token) return false;
        tokenStore.setSession(envelope.data.access_token, envelope.data.refresh_token ?? null);
        return true;
      } catch {
        return false;
      } finally {
        // Cleared on the next tick so concurrent callers all observe this run.
        setTimeout(() => {
          refreshInFlight = null;
        }, 0);
      }
    })();
  }

  return refreshInFlight;
}

async function toApiError(response: Response): Promise<ApiError> {
  let body: any = null;
  try {
    body = await response.json();
  } catch {
    // Not JSON. Fall through to the status-based message.
  }
  const message =
    typeof body?.message === 'string' && body.message.length > 0
      ? body.message
      : response.status >= 500
        ? 'Something went wrong on our side. Please try again in a moment.'
        : 'That request could not be completed.';
  return new ApiError(response.status, message, body?.error?.code, body?.error?.details);
}

async function perform(path: string, options: RequestOptions, retried = false): Promise<Response> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };

  if (!options.anonymous) {
    const token = tokenStore.token();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  if (!options.withoutOrganization) {
    const organizationId = tokenStore.organizationId();
    // A selector, not an authorisation: the server checks it against the
    // organizations this token can actually act in.
    if (organizationId) headers['X-Organization-Id'] = organizationId;
  }

  const response = await fetch(`${BASE}${path}`, {
    method: options.method ?? 'GET',
    headers,
    ...(options.data !== undefined ? { body: JSON.stringify(options.data) } : {}),
    ...(options.signal ? { signal: options.signal } : {}),
  });

  if (response.status === 401 && !options.anonymous && !retried) {
    const refreshed = await attemptRefresh();
    if (refreshed) {
      return perform(path, options, true);
    }
  }

  return response;
}

/** Perform a request and return `data`, throwing `ApiError` on failure. */
export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await perform(path, options);
  if (!response.ok) {
    throw await toApiError(response);
  }
  const envelope = (await response.json()) as ApiEnvelope<T>;
  return envelope.data;
}

/** Perform a request and return the whole envelope, for paginated reads. */
export async function apiEnvelope<T>(
  path: string,
  options: RequestOptions = {},
): Promise<ApiEnvelope<T>> {
  const response = await perform(path, options);
  if (!response.ok) {
    throw await toApiError(response);
  }
  return (await response.json()) as ApiEnvelope<T>;
}
